import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useRef } from "react";

import type { Feature, FeatureCollection, GeoJSON, Geometry } from "geojson";
import { Maximize, Minus, Plus, RotateCcw } from "lucide-react";

import { useT } from "../i18n";
import { api } from "../services/api";
import type { PlumeGrade, Satellite } from "../data/types";
import { useDataStore, type DetectionRecord } from "../store/dataStore";
import { useLayersStore } from "../store/layersStore";
import { useSelectionStore } from "../store/selectionStore";
import { useThemeStore } from "../store/themeStore";
import { IconButton } from "../components/ui/IconButton";
import { BasemapControl } from "../components/map/BasemapControl";
import { RegionControl } from "../components/map/RegionControl";
import { getBasemap } from "./basemaps";
import { readMapColors } from "./colors";
import {
  applyBasemap,
  applyColors,
  applyFilters,
  applySelection,
  applyTimeOpacity,
  applyVisibility,
  ensureLayers,
  INTERACTIVE_LAYERS,
  LYR,
  SRC,
  resetSelection,
  resetTimeFactors,
  setLayerData,
} from "./dataLayers";
import { useFiltersStore } from "../store/filtersStore";
import { useTimelineStore } from "../store/timelineStore";
import { KZ_BOUNDS, KZ_FIT_PADDING } from "./geo";
import { buildStyle } from "./style";

const EMPTY_FC: FeatureCollection = { type: "FeatureCollection", features: [] };

interface MapData {
  plumes: FeatureCollection;
  sources: FeatureCollection;
  aoi: FeatureCollection;
  regions: FeatureCollection;
  country: FeatureCollection;
}

/**
 * Coerce any GeoJSON object into a FeatureCollection. mapshaper can emit a
 * bare GeometryCollection (a dissolved shape with no attributes), which
 * geojson-vt won't render at the top level; normalizing here means the layers
 * work regardless of exactly what the build step produced.
 */
function toFeatureCollection(input: GeoJSON): FeatureCollection {
  if (input.type === "FeatureCollection") return input;
  if (input.type === "Feature") return { type: "FeatureCollection", features: [input] };
  if (input.type === "GeometryCollection") {
    const features: Feature[] = input.geometries.map((geometry) => ({
      type: "Feature",
      geometry,
      properties: {},
    }));
    return { type: "FeatureCollection", features };
  }
  // A lone geometry (Polygon, MultiPolygon, …).
  return {
    type: "FeatureCollection",
    features: [{ type: "Feature", geometry: input as Geometry, properties: {} }],
  };
}

/** Load a prebuilt boundary file; missing files are non-fatal (run build:geo). */
async function fetchGeo(url: string): Promise<FeatureCollection> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(String(res.status));
    return toFeatureCollection((await res.json()) as GeoJSON);
  } catch {
    console.warn(
      `[map] ${url} not found — run \`npm run build:geo\` to generate boundaries.`,
    );
    return EMPTY_FC;
  }
}

export function MapView() {
  const t = useT();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const dataRef = useRef<MapData>({
    plumes: EMPTY_FC,
    sources: EMPTY_FC,
    aoi: EMPTY_FC,
    regions: EMPTY_FC,
    country: EMPTY_FC,
  });

  const basemapId = useLayersStore((s) => s.basemapId);
  const flags = useLayersStore((s) => s.flags);
  const grades = useLayersStore((s) => s.grades);
  const applied = useFiltersStore((s) => s.applied);
  const resolved = useThemeStore((s) => s.resolved);
  const syncBasemapToTheme = useLayersStore((s) => s.syncBasemapToTheme);

  const selectedEpisodeId = useSelectionStore((s) => s.selectedEpisodeId);
  const selectedSourceId = useSelectionStore((s) => s.selectedSourceId);

  /** (Re)build all data layers from the current data + tokens + store state. */
  const rebuild = (map: maplibregl.Map) => {
    if (!map.isStyleLoaded()) return;
    const colors = readMapColors();
    const imagery = getBasemap(useLayersStore.getState().basemapId).imagery ?? false;
    ensureLayers(map, colors);
    setLayerData(map, dataRef.current);
    applyColors(map, colors, imagery);
    applyVisibility(map, useLayersStore.getState().flags);
    applyFilters(map, useLayersStore.getState().grades, useFiltersStore.getState().applied);
    // Feature-state is wiped by a style reload, so clear both caches before
    // re-applying: a cached "already written" entry would skip the rewrite.
    resetTimeFactors();
    resetSelection();
    applySelection(
      map,
      {
        episodeId: useSelectionStore.getState().selectedEpisodeId,
        sourceId: useSelectionStore.getState().selectedSourceId,
      },
      useDataStore.getState().records,
    );
    const tl = useTimelineStore.getState();
    // Before the timeline range exists, leave plumes at base opacity (default
    // factor 1) rather than hiding them all against a zero cursor.
    if (tl.rangeSet) applyTimeOpacity(map, useDataStore.getState().records, tl.cursor, tl.windowDays);
  };

  // Base-map application is deferred until the first load and only runs on a
  // real change, so it never touches a not-yet-loaded style (which would throw
  // on getStyle/removeLayer — the cause of the blank screen).
  const hasLoadedRef = useRef(false);
  const appliedBasemapRef = useRef(useLayersStore.getState().basemapId);
  const applyBasemapNow = (map: maplibregl.Map, id: string) => {
    appliedBasemapRef.current = id;
    const bm = getBasemap(id);
    try {
      applyBasemap(map, bm);
      applyColors(map, readMapColors(), bm.imagery ?? false);
    } catch (error) {
      console.error("[map] basemap swap failed", error);
    }
  };

  // Create the map once.
  useEffect(() => {
    if (!containerRef.current) return;
    hasLoadedRef.current = false;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildStyle(getBasemap(useLayersStore.getState().basemapId)),
      bounds: KZ_BOUNDS,
      fitBoundsOptions: { padding: KZ_FIT_PADDING },
      minZoom: 3,
      attributionControl: false,
    });
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-left");

    mapRef.current = map;
    (window as unknown as Record<string, unknown>).__map = map;
    useDataStore.getState().setMap(map);

    // Initial layer creation — `load` fires once, reliably, after the first
    // style is fully in place.
    map.on("load", () => {
      hasLoadedRef.current = true;
      rebuild(map);
      // A theme-driven swap may have changed the base map before load; apply now.
      const wanted = useLayersStore.getState().basemapId;
      if (appliedBasemapRef.current !== wanted) applyBasemapNow(map, wanted);
    });

    // Stats scoped to the current view recompute as the map moves.
    map.on("moveend", () => useDataStore.getState().bumpViewport());

    // Restore hook for base-map swaps: after each setStyle the old layers are
    // gone, so re-add them once the new style has loaded. `styledata` fires
    // many times; we act only when the style is fully loaded AND our layers are
    // actually missing, which both avoids loops (our own setData/paint updates
    // fire styledata too, but the layers exist by then) and prevents doing the
    // initial build twice with the `load` handler above.
    map.on("styledata", () => {
      if (map.isStyleLoaded() && !map.getLayer(LYR.plumeFill)) rebuild(map);
    });

    // Pointer interactions.
    const { selectEpisode, selectSource, clear, setHovered } = useSelectionStore.getState();

    map.on("click", (event) => {
      const hits = map.queryRenderedFeatures(event.point, { layers: INTERACTIVE_LAYERS });
      if (!hits.length) {
        clear();
        return;
      }
      const props = hits[0].properties as Record<string, unknown>;
      if (typeof props.episodeId === "string") selectEpisode(props.episodeId);
      else if (typeof props.sourceId === "string") selectSource(props.sourceId);
    });

    let hoveredFeature: { source: string; id: string | number } | null = null;
    const clearHoveredFeature = () => {
      if (!hoveredFeature) return;
      map.setFeatureState(hoveredFeature, { hover: false });
      hoveredFeature = null;
    };

    for (const layer of INTERACTIVE_LAYERS) {
      map.on("mouseenter", layer, (e) => {
        map.getCanvas().style.cursor = "pointer";
        const feature = e.features?.[0];
        clearHoveredFeature();
        if (feature?.id !== undefined) {
          const source = layer === LYR.plumeFill ? SRC.plumes : SRC.sources;
          hoveredFeature = { source, id: feature.id };
          map.setFeatureState(hoveredFeature, { hover: true });
        }
        const props = feature?.properties as Record<string, unknown> | undefined;
        if (props && typeof props.episodeId === "string") setHovered(props.episodeId);
      });
      map.on("mouseleave", layer, () => {
        map.getCanvas().style.cursor = "";
        clearHoveredFeature();
        setHovered(null);
      });
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") useSelectionStore.getState().clear();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
      useDataStore.getState().setMap(null);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Load data once, then push it into the map when ready.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let meta, plumes, sources, aoi, regions, country, boundary;
      try {
        [meta, plumes, sources, aoi, regions, country, boundary] = await Promise.all([
          api.getMeta(),
          api.getPlumesGeoJSON(),
          api.getSourcesGeoJSON(),
          api.getAoiGeoJSON(),
          fetchGeo("/geo/regions.geojson"),
          fetchGeo("/geo/kz.geojson"),
          api.getBoundaryGeoJSON(),
        ]);
      } catch (error) {
        console.error("[map] data load failed", error);
        if (!cancelled) useDataStore.getState().setStatus("error");
        return;
      }
      if (cancelled) return;

      // Kazakhstan keeps the design-system outline (it matches the oblast
      // borders drawn next to it); every other monitored region takes its
      // outline from the backend, which is the same boundary the data is
      // filtered against.
      const others = boundary.features.filter((f) => f.properties?.key !== "kazakhstan");
      country = {
        type: "FeatureCollection",
        features: [...country.features, ...others],
      } satisfies FeatureCollection;

      dataRef.current = { plumes, sources, aoi, regions, country };

      // Publish for the stats / filter UI: dataset bounds and flat records.
      useFiltersStore.getState().setRange(meta.dateRange.min, meta.dateRange.max);
      useDataStore.getState().setMeta(meta);
      const records: DetectionRecord[] = plumes.features.map((f) => {
        const p = f.properties as unknown as Record<string, unknown>;
        const date = String(p.date);
        return {
          id: String(p.detectionId),
          fid: f.id as number,
          episodeId: String(p.episodeId),
          date,
          dateMs: Date.parse(`${date}T00:00:00Z`),
          emissionRate: (p.emissionRate as number | null) ?? null,
          satellite: p.satellite as Satellite,
          grade: p.grade as PlumeGrade,
        };
      });
      useDataStore.getState().setRecords(records);
      useDataStore.getState().setStatus("ready");

      const map = mapRef.current;
      if (map) rebuild(map);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Theme flip offers the paired base map (unless the user pinned one).
  useEffect(() => {
    syncBasemapToTheme(resolved);
  }, [resolved, syncBasemapToTheme]);

  // Base-map swap: replace only the raster layers, keeping the data layers in
  // place (so vectors never disappear). Runs only after the first load and only
  // when the id actually changed; a change before load is handled in `load`.
  // No isStyleLoaded gate — slow raster tiles (OpenTopoMap) keep the style
  // "not loaded", and gating on that silently dropped the swap.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !hasLoadedRef.current) return;
    if (appliedBasemapRef.current === basemapId) return;
    applyBasemapNow(map, basemapId);
  }, [basemapId]);

  // Re-read tokens and repaint when the theme changes.
  useEffect(() => {
    const map = mapRef.current;
    if (map && map.isStyleLoaded()) {
      const imagery = getBasemap(useLayersStore.getState().basemapId).imagery ?? false;
      applyColors(map, readMapColors(), imagery);
    }
  }, [resolved]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && map.isStyleLoaded()) applyVisibility(map, flags);
  }, [flags]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && map.isStyleLoaded()) applyFilters(map, grades, applied);
  }, [grades, applied]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && map.isStyleLoaded()) {
      applySelection(
        map,
        { episodeId: selectedEpisodeId, sourceId: selectedSourceId },
        useDataStore.getState().records,
      );
    }
  }, [selectedEpisodeId, selectedSourceId]);

  // Timeline drives plume opacity imperatively (subscribe, not React state) so
  // playback repaints via feature-state without re-rendering this component
  // every frame.
  useEffect(() => {
    const applyNow = () => {
      const map = mapRef.current;
      if (!map || !map.isStyleLoaded()) return;
      const tl = useTimelineStore.getState();
      if (!tl.rangeSet) return;
      applyTimeOpacity(map, useDataStore.getState().records, tl.cursor, tl.windowDays);
    };
    applyNow();
    return useTimelineStore.subscribe(applyNow);
  }, []);

  const resetExtent = () =>
    mapRef.current?.fitBounds(KZ_BOUNDS, { padding: KZ_FIT_PADDING });

  const toggleFullscreen = () => {
    const el = containerRef.current?.parentElement;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen();
  };

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />

      <div className="absolute right-3 top-[4.75rem] flex flex-col items-end gap-2">
        <div data-map-overlay="true" className="flex flex-col gap-1 rounded-lg border border-border-subtle bg-surface-raised/80 p-1 shadow-md backdrop-blur">
          <IconButton label={t.map.zoomIn} onClick={() => mapRef.current?.zoomIn()}>
            <Plus size={18} />
          </IconButton>
          <IconButton label={t.map.zoomOut} onClick={() => mapRef.current?.zoomOut()}>
            <Minus size={18} />
          </IconButton>
          <div className="mx-1 my-0.5 border-t border-border-subtle" />
          <IconButton label={t.map.resetExtent} onClick={resetExtent}>
            <RotateCcw size={18} />
          </IconButton>
          <IconButton label={t.map.fullscreen} onClick={toggleFullscreen}>
            <Maximize size={18} />
          </IconButton>
        </div>
        <RegionControl />
        <BasemapControl />
      </div>
    </div>
  );
}
